import { LedgerEntry_UomSnapshot } from "gen/ledger/models_pb"
import { Product, Product_ProductUnitUsage } from "gen/product/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { Order_Product as OrderProduct } from "gen/txn/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { UUID } from "shared-ts/classes/uuid"
import {
  defaultUnitOfMeasureCode,
  defaultUnitOfMeasureLabel,
  getConversionFactorForUom,
  isValidUomSnapshot as isValidUomSnapshotShared,
  safeConvertQuantityToUoMQuantity,
  validateUomSelection,
} from "shared-ts/libs/product/unitsOfMeasure.helpers"
import { UnitOfMeasure } from "shared-ts/libs/product/unitsOfMeasure.types"
import { logger } from "shared-ts/logger"

const uomLogger = logger.withScope("pos/purchase-orders/uom")

export interface OrderUomInfo {
  uomId?: PBUUID
  conversionFactorToBase: Decimal
  displayCode: string
  displayLabel: string
}

export function getOrderDefaultUomId(product?: Product): PBUUID | undefined {
  const uomId =
    product?.productDefaultUom?.[Product_ProductUnitUsage.ORDER]?.uomId
  if (!uomId) return undefined

  const factor = getConversionFactorForUom(product, uomId)
  return factor && factor.gt(Decimal.zero()) ? uomId : undefined
}

export function isValidUomSnapshot(
  snapshot?: LedgerEntry_UomSnapshot
): snapshot is LedgerEntry_UomSnapshot {
  return isValidUomSnapshotShared(snapshot)
}

export function getOrderUomInfo(args: {
  orderProduct?: OrderProduct
}): OrderUomInfo {
  const { orderProduct } = args

  const uomId = orderProduct?.uomId
  const snap = orderProduct?.uomSnapshot

  // Snapshot policy: missing/invalid snapshot => base-only.
  if (!uomId || !isValidUomSnapshot(snap) || !snap.conversionFactorToBase) {
    return {
      uomId: undefined,
      conversionFactorToBase: Decimal.fromBigSource(1),
      displayCode: defaultUnitOfMeasureCode,
      displayLabel: defaultUnitOfMeasureLabel,
    }
  }

  const factor = Decimal.fromPB(snap.conversionFactorToBase)
  return {
    uomId,
    conversionFactorToBase: factor,
    displayCode: snap.uomCode,
    displayLabel: `${snap.uomName} (${snap.uomCode})`,
  }
}

export function buildValidatedOrderUomSelection(args: {
  product?: Product
  uomId?: PBUUID
  companyUnitsOfMeasureById: Map<string, UnitOfMeasure>
}): { uomId?: PBUUID; uomSnapshot?: LedgerEntry_UomSnapshot } {
  const { product, uomId, companyUnitsOfMeasureById } = args

  const result = validateUomSelection({
    product,
    uomId,
    companyUnitsOfMeasureById,
  })
  if (!result.ok) {
    if (result.reason !== "missing_uom_id") {
      uomLogger.error("Invalid UOM selection; falling back to base.", {
        reason: result.reason,
        productIdString: UUID.idString(product?.id),
        uomIdString: UUID.idString(uomId),
      })
    }
    return { uomId: undefined, uomSnapshot: undefined }
  }

  return { uomId: result.uomId, uomSnapshot: result.uomSnapshot }
}

export function stripOrderProductUomFields(
  orderProduct: OrderProduct
): OrderProduct {
  const {
    uomId: _uomId,
    uomSnapshot: _uomSnapshot,
    quantityOrderedUom: _quantityOrderedUom,
    ...rest
  } = orderProduct
  return rest
}

export function applyOrderUomSelection(args: {
  orderProduct: OrderProduct
}): OrderProduct {
  const { orderProduct } = args

  const uomInfo = getOrderUomInfo({ orderProduct })

  const orderedV2 = Decimal.fromPB(
    orderProduct.quantityOrderedV2 ?? Decimal.zero()
  )

  if (!uomInfo.uomId) {
    return stripOrderProductUomFields({ ...orderProduct })
  }

  return {
    ...orderProduct,
    quantityOrderedUom: safeConvertQuantityToUoMQuantity(
      orderedV2,
      uomInfo.conversionFactorToBase
    ),
  }
}

export function sanitizeOrderProductForUpsert(
  orderProduct: OrderProduct
): OrderProduct {
  if (!orderProduct.uomId) return stripOrderProductUomFields(orderProduct)

  if (!isValidUomSnapshot(orderProduct.uomSnapshot)) {
    uomLogger.error(
      "Order product has invalid/missing UOM snapshot; omitting UOM fields for upsert.",
      {
        orderProductIdString: UUID.idString(orderProduct.id),
        productIdString: UUID.idString(orderProduct.productId),
        uomIdString: UUID.idString(orderProduct.uomId),
      }
    )
    return stripOrderProductUomFields(orderProduct)
  }

  return applyOrderUomSelection({ orderProduct })
}
